import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import axiosInstance, { API_URL } from '../../utils/axios.config';

interface HealthStatus {
  backend: 'checking' | 'online' | 'offline';
  mongo: 'checking' | 'connected' | 'disconnected';
  auth: 'checking' | 'valid' | 'invalid';
  apiUrl: string;
}

const BackendHealthChecker: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus>({
    backend: 'checking',
    mongo: 'checking',
    auth: 'checking',
    apiUrl: API_URL
  });
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    checkHealth();
  }, []);

  const checkHealth = async () => {
    setChecking(true);
    
    // Check backend
    try {
      // Construct health check URL properly
      const healthUrl = API_URL.endsWith('/api') 
        ? API_URL.replace(/\/api$/, '') + '/api/health'
        : API_URL + '/health';
      
      const response = await fetch(healthUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHealth(prev => ({
          ...prev,
          backend: 'online',
          mongo: data.mongoConnected ? 'connected' : 'disconnected'
        }));
      } else {
        setHealth(prev => ({ ...prev, backend: 'offline', mongo: 'disconnected' }));
      }
    } catch (error) {
      // Silently handle - backend is expected to be offline in demo mode
      setHealth(prev => ({ ...prev, backend: 'offline', mongo: 'disconnected' }));
    }

    // Check auth
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setHealth(prev => ({ ...prev, auth: 'invalid' }));
      } else {
        const response = await axiosInstance.get('/admin/notifications/stats');
        setHealth(prev => ({ ...prev, auth: 'valid' }));
      }
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setHealth(prev => ({ ...prev, auth: 'invalid' }));
      } else {
        // If backend is offline, auth is unknown
        setHealth(prev => ({ ...prev, auth: 'checking' }));
      }
    }

    setChecking(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'checking':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'online':
      case 'connected':
      case 'valid':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'offline':
      case 'disconnected':
      case 'invalid':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'checking':
        return 'Checking...';
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Disconnected';
      case 'valid':
        return 'Valid';
      case 'invalid':
        return 'Invalid';
      default:
        return 'Unknown';
    }
  };

  const allHealthy = health.backend === 'online' && health.mongo === 'connected' && health.auth === 'valid';
  const hasIssues = health.backend === 'offline' || health.mongo === 'disconnected' || health.auth === 'invalid';

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg">System Health</h3>
        <button
          onClick={checkHealth}
          disabled={checking}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(health.backend)}
            <div>
              <div className="font-medium">Backend Server</div>
              <div className="text-sm text-gray-500">{health.apiUrl}</div>
            </div>
          </div>
          <span className="text-sm font-medium">{getStatusText(health.backend)}</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(health.mongo)}
            <div>
              <div className="font-medium">MongoDB</div>
              <div className="text-sm text-gray-500">Database Connection</div>
            </div>
          </div>
          <span className="text-sm font-medium">{getStatusText(health.mongo)}</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(health.auth)}
            <div>
              <div className="font-medium">Authentication</div>
              <div className="text-sm text-gray-500">Admin Access</div>
            </div>
          </div>
          <span className="text-sm font-medium">{getStatusText(health.auth)}</span>
        </div>
      </div>

      {hasIssues && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="font-medium text-red-800 mb-2">⚠️ Issues Detected</h4>
          <ul className="text-sm text-red-700 space-y-1">
            {health.backend === 'offline' && (
              <li>• Backend server is not running. Run: <code className="bg-red-100 px-1 rounded">cd backend && npm start</code></li>
            )}
            {health.mongo === 'disconnected' && (
              <li>• MongoDB is not connected. Check your MONGODB_URI in backend/.env</li>
            )}
            {health.auth === 'invalid' && (
              <li>• Authentication token is invalid or expired. Try logging out and logging back in.</li>
            )}
          </ul>
        </div>
      )}

      {allHealthy && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">All systems operational!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackendHealthChecker;